import React, {Component, ReactElement, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {AdminDefaultButton} from '../../styledComponents/adminPage/Buttons'
import styled, {css} from 'styled-components'
import {Box, Dialog, Table, TableBody, TableCell, TableRow} from '@mui/material';
import {LeftColumnFlexContainer, LeftRowFlexContainer} from '../../styledComponents/FlexComponents'
import {FontSizeInPixel, FontWeights} from '../../styledComponents/Constants'
import {Nullable, Optional} from '../../app/TypeHelpers'
import {AssocDirector} from './model/AssocDirector'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import moment, {Moment} from 'moment'
import {
    NatGetCurrentDateTime,
} from '../../__NoAutomatedTests/components/currentDateTime/repository/NatGetCurrentDateTime'
import {KEY_LOGIN_USER_ROLE_ID, SessionStorageHelper} from '../../SessionStorageHelper'
import {ROLE_FOR_ADMINISTRATOR, ROLE_FOR_ADMINISTRATOR_REAL} from '../../CodeMaster'
import {DeleteLink, EditLink} from '../../styledComponents/adminPage/Links'
import {HKConstants} from '../../HKConstants'
import AuthorityChecker from '../AuthorityChecker'
import AssocDirectorRepository, {NoAssocTermID} from './AssocDirectorRepository'
import {Messages} from '../../Messages'
import {Information} from '../../network/response/Information'
import {formatDateLocalSlashSeparator} from '../../formatters/formatMoment'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import AssocManagementRepository from './AssocManagementRepository'
import {AssocTerm} from './model/AssocTerm'
import {isNoSelect, NoSelectedValue} from '../../componentHelper/selectHelper'
import PPSelect, {OptionProps} from '../shared/forms/PPSelect'
import {AssocDirectorHelper} from './utils/AssocDirectorHelper'
import AdminAssocDirectorEditDialog from './components/AdminAssocDirectorEditDialog'
import {AssocOfCondominium} from './model/AssocOfCondominium'
import {RegisterAssocDirectorLocationState} from './AdminRegisterAssocDirectorPage'
import {AssocDirectorPositionsPageLocationState} from './AdminAssocDirectorPositionListPage'
import {convertAssocListToAssocSelectList, FitPageMainArea, NoWrapSpan, WidthBuffer} from './shared/AssocComponents'
import {LightGrayTableHead} from '../../styledComponents/adminPage/Table'
import {judgeAuthorityCheck, judgeRoleCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import {KEY_ASSOC_DIRECTOR_LIST_SEARCH_CONDITION} from '../../app/AdminSessionUtil'
import {SelectChangeEvent} from '@mui/material/Select'
import {Location} from 'react-router-dom'

interface SearchCondition {
    assocId: number,
    assocTermId: number | typeof NoAssocTermID,
}

interface Props {
    condominiumCd: string
    assocManagementRepo: AssocManagementRepository
    assocDirectorRepo: AssocDirectorRepository
    currentDateTimeRepo: NatCurrentDateTimeRepo
    location: Location<AssocDirectorListLocationState>
    eventEmitter: EventEmitterExAdmin
}

interface State {
    isLoadComplete: boolean
    assocDirectorList: AssocDirector[]
    currentDateTime: Nullable<Moment>
    loginUserRoleId: Nullable<string>
    assocList: AssocOfCondominium[]
    assocSelectList: OptionProps[]
    selectedAssocId: number
    assocTermList: Nullable<AssocTerm>[]
    selectedAssocTermId: number | typeof NoAssocTermID
    editBaseAssocDirector: Nullable<AssocDirector>
}

interface InitialDisplay {
    assocId: number,
    assocTermId: number,
}

export interface AssocDirectorListLocationState {
    information?: Information
    initialDisplay?: InitialDisplay
}

class AdminAssocDirectorListPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            isLoadComplete: false,
            assocDirectorList: [],
            currentDateTime: null,
            loginUserRoleId: SessionStorageHelper.getStringItemOrNull(KEY_LOGIN_USER_ROLE_ID),
            assocList: [],
            assocSelectList: [],
            selectedAssocId: 0,
            assocTermList: [],
            selectedAssocTermId: 0,
            editBaseAssocDirector: null,
        }

        this.handleChangeSelectedAssoc = this.handleChangeSelectedAssoc.bind(this)
        this.handleChangeSelectedAssocTerm = this.handleChangeSelectedAssocTerm.bind(this)
        this.handleClickSubmitOnUserEditDialog = this.handleClickSubmitOnUserEditDialog.bind(this)
        this.handleClickCancelOnUserEditDialog = this.handleClickCancelOnUserEditDialog.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const locationState = this.props.location.state
        this.props.navigate(this.props.location.pathname, {replace: true})

        await this.initContents(locationState?.initialDisplay)

        const information = locationState?.information
        if (information !== undefined && information !== null) {
            await this.props.eventEmitter.showHeadingAreaMessage({
                title: information.title ?? '',
                messageLevel: information.type,
                detail: information.message,
            })
        }

        this.setState({isLoadComplete: true})
    }

    componentDidUpdate(prevProps: Props, prevState: State): void {
        if (prevState.selectedAssocId !== this.state.selectedAssocId) {
            const searchCondition =
                SessionStorageHelper.getObject<SearchCondition>(KEY_ASSOC_DIRECTOR_LIST_SEARCH_CONDITION)
            SessionStorageHelper.setObject<SearchCondition>(
                KEY_ASSOC_DIRECTOR_LIST_SEARCH_CONDITION,
                {
                    assocId: this.state.selectedAssocId,
                    assocTermId: searchCondition?.assocTermId ?? 0,
                },
            )
        }
        if (prevState.selectedAssocTermId !== this.state.selectedAssocTermId) {
            const searchCondition =
                SessionStorageHelper.getObject<SearchCondition>(KEY_ASSOC_DIRECTOR_LIST_SEARCH_CONDITION)
            SessionStorageHelper.setObject<SearchCondition>(
                KEY_ASSOC_DIRECTOR_LIST_SEARCH_CONDITION,
                {
                    assocId: searchCondition?.assocId ?? this.state.selectedAssocId,
                    assocTermId: this.state.selectedAssocTermId,
                },
            )
        }
    }

    async initContents(initialDisplay: Optional<InitialDisplay>): Promise<void> {
        const {
            condominiumCd,
            asyncCallFunction,
            eventEmitter,
            assocManagementRepo,
            navigate,
        } = this.props

        await eventEmitter.showLoading()

        try {
            const assocList: Nullable<AssocOfCondominium[]> =
                await asyncCallFunction(
                    assocManagementRepo.getAssocList.bind(assocManagementRepo, condominiumCd),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )
            if (assocList === null) {
                return
            }
            if (assocList.length === 0) {
                navigate('/admin/error')
                return
            }

            const assocSelectList = convertAssocListToAssocSelectList(assocList)

            let selectedAssocId = assocList[0].assocId
            let initialDisplayAssocTermId: Optional<number>
            let sessionStorageAssocTermId: Optional<number | typeof NoAssocTermID>
            if (initialDisplay) {
                if (assocList.some(assoc => assoc.assocId === initialDisplay.assocId)) {
                    selectedAssocId = initialDisplay.assocId
                    initialDisplayAssocTermId = initialDisplay.assocTermId
                } else {
                    this.props.navigate(`/admin/error`, {state: {message: Messages.ESYCC0017.toString()}})
                    return
                }
            } else {
                const searchCondition =
                    SessionStorageHelper.getObject<SearchCondition>(KEY_ASSOC_DIRECTOR_LIST_SEARCH_CONDITION)

                if (searchCondition) {
                    const sessionStorageAssocId = searchCondition.assocId
                    if (assocList.some(assoc => assoc.assocId === sessionStorageAssocId)) {
                        selectedAssocId = sessionStorageAssocId
                        sessionStorageAssocTermId = searchCondition.assocTermId !== 0
                            ? searchCondition.assocTermId : undefined
                    }
                }
            }

            this.setState({
                assocList: assocList,
                assocSelectList: assocSelectList,
                selectedAssocId: selectedAssocId,
            })

            await this.updateAssocTermList(selectedAssocId, initialDisplayAssocTermId, sessionStorageAssocTermId)
        } finally {
            eventEmitter.closeLoading()
        }
    }

    async updateAssocTermList(
        assocId: number,
        initialDisplayAssocTermId?: number,
        sessionStorageAssocTermId?: number | typeof NoAssocTermID,
    ): Promise<void> {
        const {
            condominiumCd,
            asyncCallFunction,
            eventEmitter,
            assocManagementRepo,
            assocDirectorRepo,
            currentDateTimeRepo,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const currentDateTime: Nullable<NatGetCurrentDateTime> = await asyncCallFunction(
                currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (currentDateTime === null || currentDateTime.details === null) {
                return
            }
            const currentDate = moment(currentDateTime.details.currentDateTime)

            const assocTerms: Nullable<AssocTerm[]> =
                await asyncCallFunction(assocManagementRepo.getAssocTerms.bind(
                        assocManagementRepo,
                        condominiumCd,
                        assocId,
                    ),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )
            if (!assocTerms) {
                return
            }

            const noAssocTermAssocDirectorList: Nullable<AssocDirector[]> = await asyncCallFunction(
                assocDirectorRepo.getAssocDirectors.bind(
                    assocDirectorRepo,
                    condominiumCd,
                    assocId,
                    NoAssocTermID,
                ),
                (httpErrorOrNetworkError) => {
                    httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
            if (noAssocTermAssocDirectorList === null) {
                return
            }
            const selectableAssocTermList: Nullable<AssocTerm>[] =
                noAssocTermAssocDirectorList.length !== 0 ? [null, ...assocTerms] : assocTerms

            let selectedAssocTermId: number | typeof NoAssocTermID
            if (selectableAssocTermList.length !== 0) {
                const mostResentTerm = this.getResentTerm(selectableAssocTermList, currentDate)
                selectedAssocTermId = mostResentTerm?.assocTermId ?? NoAssocTermID

                if (initialDisplayAssocTermId !== undefined) {
                    if (selectableAssocTermList
                        .some(assocTerm => (assocTerm !== null ? assocTerm.assocTermId : NoAssocTermID)
                            === initialDisplayAssocTermId)) {
                        selectedAssocTermId = initialDisplayAssocTermId
                    } else {
                        this.props.navigate(`/admin/error`, {state: {message: Messages.ESYCC0017.toString()}})
                        return
                    }
                } else {
                    if (sessionStorageAssocTermId !== undefined) {
                        if (selectableAssocTermList
                            .some(assocTerm => (assocTerm !== null ? assocTerm.assocTermId : NoAssocTermID)
                                === sessionStorageAssocTermId)) {
                            selectedAssocTermId = sessionStorageAssocTermId
                        }
                    }
                }
            } else {
                selectedAssocTermId = 0
            }

            this.setState({
                assocTermList: selectableAssocTermList,
                selectedAssocTermId: selectedAssocTermId,
            })

            if (selectedAssocTermId === 0) {
                return
            }
            await this.updateAssocDirectorList(assocId, selectedAssocTermId)
        } finally {
            eventEmitter.closeLoading()
        }
    }

    private getResentTerm(assocTerms: Nullable<AssocTerm>[], currentDate: Moment): Nullable<AssocTerm> {
        const currentTerms = assocTerms
            .filter(term =>
                term !== null &&
                term.assocTermStartDate.isSameOrBefore(currentDate, 'd') &&
                term.assocTermEndDate.isSameOrAfter(currentDate, 'd'),
            )
        if (currentTerms.length > 0) {
            return currentTerms[0]
        }

        const futureTerms = assocTerms.filter(term =>
            term !== null &&
            term.assocTermStartDate.isAfter(currentDate, 'd'),
        )
        if (futureTerms.length > 0) {
            return futureTerms[futureTerms.length - 1]
        }

        const pastTerms = assocTerms.filter(term =>
            term !== null &&
            term.assocTermEndDate.isBefore(currentDate, 'd'),
        )
        if (pastTerms.length > 0) {
            return pastTerms[0]
        }

        return assocTerms[0]
    }

    async updateAssocDirectorList(assocId: number, assocTermId: number | typeof NoAssocTermID): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const currentDateTime: Nullable<NatGetCurrentDateTime> = await this.props.asyncCallFunction(
                this.props.currentDateTimeRepo.getCurrentDateTime.bind(this.props.currentDateTimeRepo),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (currentDateTime === null || currentDateTime.details === null) {
                return
            }

            const assocDirectorList: Nullable<AssocDirector[]> =
                await this.props.asyncCallFunction(
                    this.props.assocDirectorRepo.getAssocDirectors.bind(
                        this.props.assocDirectorRepo,
                        this.props.condominiumCd,
                        assocId,
                        assocTermId,
                    ),
                    (httpErrorOrNetworkError) => {
                        httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    },
                )
            if (assocDirectorList === null) {
                return
            }

            this.setState({
                assocDirectorList: assocDirectorList,
                currentDateTime: moment(currentDateTime.details.currentDateTime),
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    getSelectedAssocTerm(
        selectedAssocTermId: number | typeof NoAssocTermID,
        assocTermList: Nullable<AssocTerm>[],
    ): Nullable<AssocTerm> {
        return assocTermList.find(term => {
            if (selectedAssocTermId !== NoAssocTermID) {
                return term?.assocTermId === selectedAssocTermId
            } else {
                return term === null
            }
        }) ?? null
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    async handleChangeSelectedAssoc(event: SelectChangeEvent<unknown>): Promise<void> {
        this.beforeUserAction()

        if (!event.target.value) {
            return
        }
        const value: number = Number(event.target.value)

        this.setState({
            selectedAssocId: value,
            assocDirectorList: [],
        })

        await this.updateAssocTermList(value)
    }

    async handleChangeSelectedAssocTerm(event: SelectChangeEvent<unknown>): Promise<void> {
        this.beforeUserAction()

        const value = event.target.value
        const selectId = isNoSelect(value) ? NoAssocTermID : Number(value)

        this.setState({selectedAssocTermId: selectId})

        await this.updateAssocDirectorList(this.state.selectedAssocId, selectId)
    }

    handleClickRegisterAssocDirector = (
        assocList: AssocOfCondominium[],
        selectedAssocId: number,
        selectedAssocTerm: AssocTerm,
    ) => () => {
        this.beforeUserAction()
        const locationState: RegisterAssocDirectorLocationState = {
            assocList: assocList,
            selectedAssocId: selectedAssocId,
            selectedAssocTerm: selectedAssocTerm.toObject(),
        }
        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/assoc-directors/register`,
            {state: locationState},
        )
    }

    handleClickAssocDirectorPositions = () => {
        this.beforeUserAction()

        const {assocList, selectedAssocId, assocTermList, selectedAssocTermId} = this.state
        if (selectedAssocTermId === 0) {
            return
        }

        const selectedAssocTerm = this.getSelectedAssocTerm(selectedAssocTermId, assocTermList)
        if (selectedAssocTerm === null) {
            return
        }

        let displayAssocName: Nullable<string> = null
        if (assocList.length >= 2) {
            displayAssocName = assocList.find(assoc => assoc.assocId === selectedAssocId)?.assocName ?? null
        }

        const locationState: AssocDirectorPositionsPageLocationState = {
            displayAssocName: displayAssocName,
            selectedAssocId: selectedAssocId,
            selectedAssocTerm: selectedAssocTerm.toObject(),
        }
        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/assoc-director-positions`,
            {state: locationState},
        )
    }

    handleClickEditAssocDirector = (assocDirector: AssocDirector) => () => {
        this.beforeUserAction()
        this.setState({editBaseAssocDirector: assocDirector})
    }

    async handleClickSubmitOnUserEditDialog(information: Information): Promise<void> {
        await this.props.eventEmitter.showHeadingAreaMessage({
            title: information.title ?? '',
            messageLevel: information.type,
            detail: information.message,
        })
        this.setState({editBaseAssocDirector: null})
        await this.updateAssocDirectorList(this.state.selectedAssocId, this.state.selectedAssocTermId)
    }

    handleClickCancelOnUserEditDialog(): void {
        this.setState({editBaseAssocDirector: null})
    }

    handleClickDeleteAssocDirector = (
        assocDirectorId: number,
        selectedAssocId: number,
        selectedAssocTermId: number | typeof NoAssocTermID,
        termStartDate: Nullable<Moment>,
    ) => () => {
        this.beforeUserAction()

        const {currentDateTime} = this.state
        let errorLevel = Messages.IOPCC0011.errorLevel
        let message = Messages.IOPCC0011.toString('管理組合役員')
        if (this.state.loginUserRoleId === ROLE_FOR_ADMINISTRATOR.SYSTEM_ADMIN.value) {
            if (termStartDate !== null && currentDateTime !== null && termStartDate.isSameOrBefore(currentDateTime)) {
                errorLevel = Messages.IASMA0004.errorLevel
                message = Messages.IASMA0004.toString()
            }
        }
        this.props.eventEmitter.showDialog({
            title: '確認',
            errorLevel: errorLevel,
            message: message,
            buttonProps: [
                {
                    isDefault: false,
                    buttonLabel: 'キャンセル',
                    buttonHandler: () => this.props.eventEmitter.closeDialog(),
                },
                {
                    isDefault: false,
                    buttonLabel: 'OK',
                    buttonHandler: this.handleClickOkOnAssocDirectorDeleteConfirmDialog(
                        assocDirectorId,
                        selectedAssocId,
                        selectedAssocTermId,
                    ),
                },
            ],
        })
    }

    handleClickOkOnAssocDirectorDeleteConfirmDialog = (
        assocDirectorId: number,
        selectedAssocId: number,
        selectedAssocTermId: number | typeof NoAssocTermID,
    ) => async () => {
        const {condominiumCd, eventEmitter, asyncCallFunction, assocDirectorRepo} = this.props
        eventEmitter.closeDialog()

        await eventEmitter.showLoading()
        try {
            const deleteMessage: Nullable<Information> = await asyncCallFunction(
                assocDirectorRepo.deleteAssocDirector.bind(
                    assocDirectorRepo,
                    condominiumCd,
                    selectedAssocId,
                    selectedAssocTermId,
                    assocDirectorId,
                ),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (deleteMessage === null) {
                return
            }

            await eventEmitter.showHeadingAreaMessage({
                title: deleteMessage.title || '',
                messageLevel: deleteMessage.type,
                detail: deleteMessage.message,
            })

            if (this.state.selectedAssocTermId === 0) {
                return
            }
            await this.updateAssocDirectorList(selectedAssocId, this.state.selectedAssocTermId)
        } finally {
            eventEmitter.closeLoading()
        }
    }

    renderPosition(
        positionName: string,
        midtermAppointmentDate: Nullable<Moment>,
        midtermTerminationDate: Nullable<Moment>,
    ):
        ReactNode {
        if (midtermAppointmentDate && midtermTerminationDate) {
            return <>
                <NoWrapSpan>
                    {`${positionName}（${midtermAppointmentDate.format('YYYY/MM/DD')} `}
                </NoWrapSpan>
                <NoWrapSpan>
                    {`～ ${midtermTerminationDate.format('YYYY/MM/DD')}）`}
                </NoWrapSpan>
            </>
        } else if (midtermAppointmentDate) {
            return <NoWrapSpan>
                {`${positionName}（${midtermAppointmentDate.format('YYYY/MM/DD')} ～）`}
            </NoWrapSpan>

        } else if (midtermTerminationDate) {
            return <NoWrapSpan>
                {`${positionName}（～ ${midtermTerminationDate.format('YYYY/MM/DD')}）`}
            </NoWrapSpan>
        } else {
            return <NoWrapSpan>
                {positionName}
            </NoWrapSpan>
        }
    }

    renderAssocDirectorRecord(assocDirector: AssocDirector): ReactNode {
        const {
            currentDateTime,
            loginUserRoleId,
            selectedAssocId,
            selectedAssocTermId,
        } = this.state
        if (currentDateTime === null) {
            return null
        }

        const roomNumberNode: ReactElement =
            AssocDirectorHelper.getRoomNumberNodeByResidentStatus(assocDirector.roomList)

        const displayUserName: ReactNode =
            AssocDirectorHelper.getDisplayUserNameNodeByResidentStatus(
                assocDirector.familyName, assocDirector.firstName, assocDirector.roomList,
            )

        const displayPositionNode: ReactNode = assocDirector.assocDirectorPositionInfoList
            .map((info, index) =>
                <div key={`position-${index}`}>
                    {this.renderPosition(
                        ROLE_FOR_ADMINISTRATOR_REAL.codeToEnumConstants(info.positionRoleId).label,
                        info.midtermAppointmentDate,
                        info.midtermTerminationDate,
                    )}
                </div>)

        const {availableTermStartDate, availableTermEndDate} = assocDirector

        let showEditLink: boolean = false
        let showDeleteLink: boolean = false
        switch (loginUserRoleId) {
            case ROLE_FOR_ADMINISTRATOR.SYSTEM_ADMIN.value:
                showEditLink = true
                showDeleteLink = true
                break
            case ROLE_FOR_ADMINISTRATOR.FRONT_STAFF.value:
            case ROLE_FOR_ADMINISTRATOR.LIFE_MANAGER.value:
            case ROLE_FOR_ADMINISTRATOR.CALL_CENTER.value:
            case ROLE_FOR_ADMINISTRATOR.CONCIERGE.value:
                if (availableTermEndDate.isSameOrAfter(currentDateTime, 'd')) {
                    showEditLink = true
                }
                if (availableTermStartDate.isAfter(currentDateTime, 'd')) {
                    showDeleteLink = true
                }
                break
            default:
        }
        if (selectedAssocTermId === NoAssocTermID) {
            showEditLink = false
        }

        return (
            <AssocDirectorTableRow key={`director-${assocDirector.assocDirectorId}`}>
                <RoomInfoCell>{roomNumberNode}</RoomInfoCell>
                <NameCell>{displayUserName}</NameCell>
                <TermCell>
                    <NoWrapSpan>{`${formatDateLocalSlashSeparator(availableTermStartDate)} `}</NoWrapSpan>
                    <NoWrapSpan>{`〜 ${formatDateLocalSlashSeparator(availableTermEndDate)}`}</NoWrapSpan>
                </TermCell>
                <PositionCell>
                    <LeftRowFlexContainer>
                        <LeftColumnFlexContainer>
                            {displayPositionNode}
                        </LeftColumnFlexContainer>
                    </LeftRowFlexContainer>
                </PositionCell>
                <OperationCell>
                    {(showEditLink || showDeleteLink) &&
                        <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_ASSOC}>
                            <LeftRowFlexContainer>
                                <WidthBuffer>あああああああ</WidthBuffer>
                                {showEditLink &&
                                    <EditLink onClick={this.handleClickEditAssocDirector(assocDirector)}>
                                        編集
                                    </EditLink>
                                }
                                {showDeleteLink &&
                                    <>
                                        <Box display={'inline-block'} minWidth={'24px'}/>
                                        <DeleteLink onClick={
                                            this.handleClickDeleteAssocDirector(
                                                assocDirector.assocDirectorId,
                                                selectedAssocId,
                                                selectedAssocTermId,
                                                availableTermStartDate,
                                            )
                                        }>
                                            削除
                                        </DeleteLink>
                                    </>
                                }
                            </LeftRowFlexContainer>
                        </AuthorityChecker>}
                </OperationCell>
            </AssocDirectorTableRow>
        )
    }

    renderAssocDirectorTable(assocDirectorList: AssocDirector[]): Nullable<ReactElement> {

        return (
            <FitPageMainArea>
                <Table stickyHeader>
                    <LightGrayTableHead>
                        <AssocDirectorTableRow>
                            <RoomInfoCell>
                                <NoWrapSpan>部屋番号</NoWrapSpan>
                            </RoomInfoCell>
                            <NameCell>
                                <NoWrapSpan>氏名</NoWrapSpan>
                            </NameCell>
                            <TermCell>
                                <NoWrapSpan>役員向け機能の</NoWrapSpan>
                                <NoWrapSpan>利用期間</NoWrapSpan>
                            </TermCell>
                            <PositionCell>
                                <NoWrapSpan>役職</NoWrapSpan>
                            </PositionCell>
                            <OperationCell/>
                        </AssocDirectorTableRow>
                    </LightGrayTableHead>
                    <TableBody>
                        {assocDirectorList.map(
                            assocDirector => this.renderAssocDirectorRecord(assocDirector),
                        )}
                    </TableBody>
                </Table>
            </FitPageMainArea>
        )
    }

    render(): ReactNode {
        const {condominiumCd, assocDirectorRepo, eventEmitter} = this.props
        const {
            assocList,
            assocSelectList,
            currentDateTime,
            selectedAssocId,
            assocDirectorList,
            assocTermList,
            selectedAssocTermId,
            editBaseAssocDirector,
            isLoadComplete,
        } = this.state

        if (!isLoadComplete) {
            return null
        }

        const showAssocTermListSelect = assocTermList.length !== 0
        const selectedAssocTerm = selectedAssocTermId !== 0
            ? this.getSelectedAssocTerm(selectedAssocTermId, assocTermList) : null

        let assocDirectorListNode: ReactNode
        if (assocTermList.length === 0) {
            const assocAuthority: boolean = judgeAuthorityCheck('EDIT-ASSOC')
            assocDirectorListNode = <Box mt={'32px'}>
                <div>役員が登録されていません。</div>
                {assocAuthority && <div>役員を登録する前に、理事会の任期の登録が必要です。</div>}
            </Box>
        } else if (assocDirectorList.length === 0) {
            assocDirectorListNode = <Box mt={'32px'}>
                <div>役員が登録されていません。</div>
            </Box>
        } else {
            assocDirectorListNode = this.renderAssocDirectorTable(assocDirectorList)
        }

        const showRegisterButton = selectedAssocTerm?.assocTermEndDate.isSameOrAfter(currentDateTime)
            || judgeRoleCheck([ROLE_FOR_ADMINISTRATOR.SYSTEM_ADMIN.value])

        return (<>
            <Box height={'9px'}/>
            {assocList.length >= 2 && <>
                <label>
                    <LeftRowFlexContainer>
                        <AssocItemName>管理組合</AssocItemName>
                        <TransparentSelect
                            data={assocSelectList}
                            value={String(selectedAssocId)}
                            onChange={this.handleChangeSelectedAssoc}
                        />
                    </LeftRowFlexContainer>
                </label>
                <Box height={'8px'}/>
            </>}
            {showAssocTermListSelect &&
                <label>
                    <LeftRowFlexContainer>
                        <AssocTermItemName>理事会の任期</AssocTermItemName>
                        <TransparentSelect
                            onChange={this.handleChangeSelectedAssocTerm}
                            value={selectedAssocTermId ? String(selectedAssocTermId) : NoSelectedValue}
                            data={assocTermList.map(term => ({
                                label: AssocDirectorHelper.getDisplayAssocTerm(term),
                                value: term ? `${term.assocTermId}` : NoSelectedValue,
                            }))}
                        />
                    </LeftRowFlexContainer>
                </label>
            }
            {
                showRegisterButton && selectedAssocTerm !== null &&
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_ASSOC}>
                    <ActionArea>
                        <RegisterButton
                            onClick={this.handleClickRegisterAssocDirector(
                                assocList,
                                selectedAssocId,
                                selectedAssocTerm,
                            )}>
                            役員を登録する
                        </RegisterButton>
                        {assocDirectorList.length > 0 &&
                            <>
                                <Box width={'16px'}/>
                                <RegisterButton
                                    onClick={this.handleClickAssocDirectorPositions}>
                                    役職を設定する
                                </RegisterButton>
                            </>
                        }
                    </ActionArea>
                </AuthorityChecker>
            }
            <Box mt={'16px'}>{assocDirectorListNode}</Box>
            {editBaseAssocDirector && selectedAssocTerm !== null &&
                <Dialog
                    id={'pp-dialog'}
                    open={true}
                    transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                    maxWidth={'md'}
                >
                    <AdminAssocDirectorEditDialog
                        condominiumCd={condominiumCd}
                        assocTerm={selectedAssocTerm}
                        assocList={assocList}
                        selectedAssocId={selectedAssocId}
                        prevAssocDirector={editBaseAssocDirector}
                        assocDirectorRepo={assocDirectorRepo}
                        onSubmit={this.handleClickSubmitOnUserEditDialog}
                        onCancel={this.handleClickCancelOnUserEditDialog}
                        eventEmitter={eventEmitter}
                    />
                </Dialog>
            }
        </>)
    }
}

export default createAdminPage(AdminAssocDirectorListPage)

const ActionArea = styled(LeftRowFlexContainer)`
  margin: 16px 0 0 0;
`

const AssocItemName = styled.div`
  min-width: 124px;
`

const TransparentSelect = styled(PPSelect)`
  &.MuiInputBase-root {
    background-color: initial;
    font-weight: 700;
  }

  .MuiSelect-root {
    background-color: initial;
  }

  .MuiSelect-select {
    min-width: 343px;
    font-weight: ${FontWeights.Bold};
    font-size: ${FontSizeInPixel.Body}px;
  }
`

const AssocTermItemName = styled.div`
  min-width: 124px;
`

const RegisterButton = styled(AdminDefaultButton)`
  width: 162px;
`

const AssocDirectorTableRow = styled(TableRow)`
  & > .MuiTableCell-root:first-child {
    padding-left: 40px;
  }

  & > .MuiTableCell-root:last-child {
    padding-right: 40px;
  }
`

const TableCellBaseCss = css`
  padding: 16px 0;
`

const RoomInfoCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    padding-right: 12px;
  }
`

const NameCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    padding-left: 12px;
    padding-right: 20px;
    max-width: 172px;
  }
`

const TermCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    padding-left: 20px;
    padding-right: 20px;
    line-height: 24px;
  }
`

const PositionCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    padding-left: 20px;
    line-height: 24px;
  }
`

const OperationCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    white-space: nowrap;
  }
`
